import PropTypes from 'prop-types';
import { useMemo } from 'react';

import { ComplianceHeader } from './ComplianceHeader';
import { toR } from './constants';
import Table from '../../../../ComponentLibrary/oc-table';
import { connectLocation } from '../../../../location/LocationConnectors';
import { SummaryInline, ResultsCard, FlexRow } from '../../styles';

const safe = val => {
  if (typeof val !== 'number' || !Number.isFinite(val)) return '-';

  // If it's an integer, return as is
  if (Number.isInteger(val)) return val;

  // If it's a float, round to 2 decimals & remove trailing zeros
  return parseFloat(val.toFixed(2));
};

const resolveRSI = (row, results) => {
  if (!results) return null;
  if (typeof row.rsi === 'function') return row.rsi(results);
  if (typeof row.rsi === 'string') return results[row.rsi];
  return null;
};

const displayCell = (row, results) => {
  if (typeof row.text === 'function') return row.text(results);
  const rsi = resolveRSI(row, results);
  return safe(rsi);
};

const ResultsBlock = ({ t, province, region, hdd, results, rows, tableTitle, tableSubtitle, error, calcId }) => {
  const visibleRows = useMemo(() => rows.filter(row => (typeof row.show === 'function' ? !!row.show(results) : true)), [
    rows,
    results,
  ]);

  return (
    <div>
      {error && (
        <div
          role="alert"
          style={{
            marginBottom: 12,
            padding: '10px 12px',
            background: '#fff4f4',
            border: '1px solid #f5c2c7',
            color: '#842029',
            borderRadius: 6,
            fontWeight: 600,
          }}
        >
          {error}
        </div>
      )}
      <p style={{ marginBottom: 32, marginTop: 0, fontSize: 28, fontWeight: 700 }}>
        {t('thermal_calculator.results.title')}
      </p>

      <FlexRow justify="space-between">
        <SummaryInline>
          <div>
            <strong>{t('thermal_calculator.results.province')}</strong>
          </div>
          <div>{province || '—'}</div>

          <div>
            <strong>{t('thermal_calculator.results.region')}</strong>
          </div>
          <div>{region || '—'}</div>

          <div>
            <strong>{t('thermal_calculator.results.heating_degree_days')}</strong>
          </div>
          <div>{hdd ?? '—'}</div>
        </SummaryInline>
        <ComplianceHeader hdd={hdd} results={results} calcId={calcId} />
      </FlexRow>

      <ResultsCard>
        <Table striped bordered fixedTableLayout firstLeft margin="0" textTransform="none">
          <thead>
            <tr>
              <th className="left-align">
                {tableTitle || t('thermal_calculator.entire_assembly')}
                <br />({tableSubtitle || t('thermal_calculator.effective_thermal_resistance')})
              </th>
              <th style={{ width: 140 }}>RSI</th>
              <th style={{ width: 140 }}>R</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map(row => {
              const rsi = resolveRSI(row, results);
              return (
                <tr key={row.label}>
                  <td>
                    <b>{row.label}</b>
                  </td>
                  <td style={{ textAlign: 'center' }}>{displayCell(row, results)}</td>
                  <td style={{ textAlign: 'center' }}>
                    {row.noR || typeof row.text === 'function' ? '' : safe(rsi != null ? toR(rsi, row.roundR) : null)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </ResultsCard>
    </div>
  );
};

ResultsBlock.propTypes = {
  calcId: PropTypes.string.isRequired,
  province: PropTypes.string,
  region: PropTypes.string,
  hdd: PropTypes.number,
  // eslint-disable-next-line react/forbid-prop-types
  results: PropTypes.object,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      rsi: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
      show: PropTypes.func,
      noR: PropTypes.bool,
      roundR: PropTypes.bool,
      text: PropTypes.func, // when provided, overrides RSI cell with a textual value
    })
  ).isRequired,
  tableTitle: PropTypes.string,
  tableSubtitle: PropTypes.string,
  error: PropTypes.string,
  t: PropTypes.func.isRequired,
};

ResultsBlock.defaultProps = {
  province: '',
  region: '',
  hdd: null,
  results: null,
  tableTitle: '',
  tableSubtitle: '',
  error: null,
};

export default connectLocation(ResultsBlock);
